import React, { useState, useEffect, useCallback } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Bell, Check, X, UserPlus, Sparkles, HeartHandshake, Heart, History, Pencil, Trash2, Loader2 } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const typeIcon = { request: HeartHandshake, suggestion: Sparkles, join: UserPlus, donation: Heart };
const sugIcon = { add: UserPlus, edit: Pencil, delete: Trash2 };

export function NotificationsPanel({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("notif");
  const [data, setData] = useState({ items: [], badge: 0, counts: {} });
  const [activity, setActivity] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try { setData(await api.getNotifications()); } catch (e) { console.error("Failed to load notifications", e); }
  }, []);
  const loadActivity = useCallback(async () => {
    try { setActivity((await api.listActivity()).activity || []); } catch (e) { console.error("Failed to load activity log", e); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (open) { load(); if (tab === "activity") loadActivity(); } }, [open, tab, load, loadActivity]);

  const act = async (item, action) => {
    setBusyId(item.id);
    try {
      if (item.type === "suggestion") {
        action === "approve" ? await api.approveSuggestion(item.ref_id) : await api.rejectSuggestion(item.ref_id);
        toast.success(action === "approve" ? "Suggestion applied to the tree" : "Suggestion dismissed");
        onChanged?.();
      } else if (item.type === "request") {
        if (action === "approve") { await api.approveContribRequest(item.ref_id); toast.success("Approved — an invite link is ready in the Members panel"); }
        else { await api.declineContribRequest(item.ref_id); toast.success("Request declined"); }
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete that");
    } finally { setBusyId(null); }
  };

  const actionable = data.items.filter((i) => i.actionable);
  const recent = data.items.filter((i) => !i.actionable);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid="notifications-bell" variant="ghost" className="relative rounded-full text-[#64748B] hover:text-[#082D4A]">
          <Bell size={18} />
          {data.badge > 0 && (
            <span data-testid="notifications-badge" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#D4A017] px-1 text-[10px] font-bold text-white">{data.badge}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] max-w-[92vw] p-0" data-testid="notifications-panel">
        <div className="flex items-center gap-1 border-b border-[#D8E0E8] p-2">
          <button onClick={() => setTab("notif")} data-testid="notif-tab" className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-medium ${tab === "notif" ? "bg-[#E8EEF3] text-[#082D4A]" : "text-[#64748B]"}`}>Notifications</button>
          <button onClick={() => setTab("activity")} data-testid="activity-tab" className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-medium ${tab === "activity" ? "bg-[#E8EEF3] text-[#082D4A]" : "text-[#64748B]"}`}>Activity log</button>
        </div>

        {tab === "notif" ? (
          <div className="max-h-[70vh] overflow-y-auto p-2">
            {data.items.length === 0 && <div className="p-6 text-center text-sm text-[#94A3B8]">You’re all caught up 🎉</div>}
            {actionable.length > 0 && <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wider text-[#64748B]">Needs your attention</div>}
            {actionable.map((item) => {
              const Icon = item.type === "suggestion" ? (sugIcon[item.sug_type] || Sparkles) : (typeIcon[item.type] || Bell);
              return (
                <div key={item.id} data-testid={`notif-${item.id}`} className="mb-1.5 rounded-xl border border-[#D8E0E8] bg-white p-2.5">
                  <div className="flex gap-2">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#E8EEF3] text-[#082D4A]"><Icon size={15} /></span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[#082D4A]">{item.title}</div>
                      {item.subtitle && <div className="truncate text-xs text-[#64748B]">{item.subtitle}</div>}
                      {item.type === "suggestion" && item.diff?.length > 0 && (
                        <div className="mt-1.5 space-y-0.5 rounded-lg bg-[#F4F6F8] p-2" data-testid={`notif-diff-${item.id}`}>
                          {item.diff.slice(0, 6).map((d, i) => (
                            <div key={d.field || i} className="text-[11px] leading-tight">
                              <span className="font-medium capitalize text-[#64748B]">{String(d.field).replace(/_/g, " ")}: </span>
                              {item.sug_type === "add" ? (
                                <span className="text-[#082D4A]">{String(d.new)}</span>
                              ) : (
                                <><span className="text-red-500 line-through">{d.old ? String(d.old) : "—"}</span>
                                <span className="text-[#94A3B8]"> → </span>
                                <span className="text-[#007F6B]">{d.new ? String(d.new) : "—"}</span></>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-0.5 text-[10px] text-[#94A3B8]">{timeAgo(item.created_at)}</div>
                    </div>
                  </div>
                  <div className="mt-2 flex gap-1.5">
                    <Button size="sm" disabled={busyId === item.id} onClick={() => act(item, "approve")} data-testid={`notif-approve-${item.id}`} className="h-7 flex-1 bg-[#082D4A] text-white hover:bg-[#08223A]">
                      {busyId === item.id ? <Loader2 size={13} className="animate-spin" /> : <><Check size={13} className="mr-1" /> Approve</>}
                    </Button>
                    <Button size="sm" variant="outline" disabled={busyId === item.id} onClick={() => act(item, "reject")} data-testid={`notif-reject-${item.id}`} className="h-7 border-[#D8E0E8] px-2">
                      <X size={13} />
                    </Button>
                  </div>
                </div>
              );
            })}
            {recent.length > 0 && <div className="px-2 py-1 pt-3 text-xs font-semibold uppercase tracking-wider text-[#64748B]">Recent</div>}
            {recent.map((item) => {
              const Icon = typeIcon[item.type] || Bell;
              return (
                <div key={item.id} data-testid={`notif-${item.id}`} className="mb-1.5 flex gap-2 rounded-xl px-2.5 py-2">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#E8EEF3] text-[#082D4A]"><Icon size={15} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#082D4A]">{item.title}</div>
                    {item.subtitle && <div className="truncate text-xs text-[#64748B]">{item.subtitle}</div>}
                    <div className="mt-0.5 text-[10px] text-[#94A3B8]">{timeAgo(item.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="max-h-[70vh] overflow-y-auto p-2" data-testid="activity-list">
            {activity.length === 0 && <div className="p-6 text-center text-sm text-[#94A3B8]"><History size={20} className="mx-auto mb-2 opacity-40" />No activity yet</div>}
            {activity.map((a) => (
              <div key={a.id} className="mb-1 flex gap-2 rounded-lg px-2.5 py-1.5">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D4A017]" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#475569]">{a.summary || `${a.action} ${a.member_name || ""}`}</div>
                  <div className="text-[10px] text-[#94A3B8]">{a.actor_name} · {timeAgo(a.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
